<template>
  <div
    :class="[
      'fit-video-player',
      {
        'is-paused': isPaused,
        'is-unmuted': !isMuted,
      },
    ]"
  >
    <video
      ref="videoEl"
      class="fit-video-player__video"
      :muted="isMuted"
      loop
      playsinline
      preload="metadata"
      :poster="poster || undefined"
      @loadedmetadata="onLoadedMetadata"
      @play="isPaused = false"
      @pause="isPaused = true"
    >
      <source
        v-for="(source, index) in sources"
        :key="index"
        :src="source.url"
        :type="source.mime_type"
      />
    </video>

    <div class="fit-video-player__overlay" aria-hidden="true" />

    <button
      class="fit-video-player__play-btn"
      type="button"
      aria-label="Play video"
      @click.stop="playPreferringSound"
    >
      <svg width="62" height="62" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="31" cy="31" r="30" fill="white" fill-opacity="0.6" stroke="white" stroke-width="2"/>
        <path d="M43.544 27.4704C44.1844 27.811 44.7201 28.3193 45.0936 28.9411C45.4672 29.5628 45.6645 30.2744 45.6645 30.9998C45.6645 31.7251 45.4672 32.4367 45.0936 33.0584C44.7201 33.6802 44.1844 34.1885 43.544 34.5291L26.4614 43.8184C23.7107 45.3158 20.332 43.3691 20.332 40.2904V21.7104C20.332 18.6304 23.7107 16.6851 26.4614 18.1798L43.544 27.4704Z" fill="white"/>
      </svg>
    </button>

    <button
      class="fit-video-player__control fit-video-player__control--pause"
      type="button"
      aria-label="Pause video"
      @click.stop="pause"
    >
      <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M23.75 11.25V25.75H21.75V11.25H23.75ZM16.25 11.25V25.75H14.25V11.25H16.25Z" fill="white" stroke="white" stroke-width="0.5"/>
        <circle cx="19" cy="19" r="18" fill="white" fill-opacity="0.22" stroke="white" stroke-width="2"/>
      </svg>
    </button>

    <button
      class="fit-video-player__control fit-video-player__control--mute"
      type="button"
      :aria-label="isMuted ? 'Unmute video' : 'Mute video'"
      @click.stop="toggleMute"
    >
      <svg class="fit-video-player__icon-muted" width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="19" cy="19" r="18" fill="#D9D9D9" fill-opacity="0.47" stroke="white" stroke-width="2"/>
        <path d="M10.4062 15.9062V22.0938H13.8438L19.3438 26.2188V11.7812L13.8438 15.9062H10.4062Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M27.5938 15.9062L22.7812 22.0938M22.7812 15.9062L27.5938 22.0938" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <svg class="fit-video-player__icon-unmuted" width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#fitVideoUnmuteClip)">
          <path d="M19 37C28.9411 37 37 28.9411 37 19C37 9.05887 28.9411 1 19 1C9.05887 1 1 9.05887 1 19C1 28.9411 9.05887 37 19 37Z" fill="#D9D9D9" fill-opacity="0.47" stroke="white" stroke-width="2"/>
          <path d="M10.4062 15.9062V22.0938H13.8438L19.3438 26.2188V11.7812L13.8438 15.9062H10.4062Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <rect x="22" y="14" width="1.46578" height="9" rx="0.732888" fill="white"/>
          <rect x="25" y="16" width="1" height="5" rx="0.5" fill="white"/>
        </g>
        <defs>
          <clipPath id="fitVideoUnmuteClip">
            <rect width="38" height="38" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    </button>
  </div>
</template>

<script setup>
  import { ref, onMounted, onUnmounted, watch } from 'vue';

  const props = defineProps({
    sources: {
      type: Array,
      default: () => [],
    },
    poster: {
      type: String,
      default: '',
    },
    autoplay: {
      type: Boolean,
      default: true,
    },
    startUnmuted: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['ready', 'aspect-ratio']);

  const videoEl = ref(null);
  const isPaused = ref(true);
  const isMuted = ref(!props.startUnmuted);

  const setMuted = (muted) => {
    const video = videoEl.value;
    if (video) video.muted = muted;
    isMuted.value = muted;
  };

  const play = () => {
    const video = videoEl.value;
    if (!video) return Promise.resolve();
    return video.play().catch(() => {});
  };

  // Unmuted autoplay is blocked without a user gesture (often expired after
  // the gallery click opens this dialog). Try sound, then fall back to muted.
  const playPreferringSound = () => {
    setMuted(false);
    const video = videoEl.value;
    if (!video) return Promise.resolve();
    return video.play().catch(() => {
      setMuted(true);
      return play();
    });
  };

  const tryAutoplay = () => {
    if (props.startUnmuted) return playPreferringSound();
    setMuted(true);
    return play();
  };

  const pause = () => {
    videoEl.value?.pause();
  };

  const toggleMute = () => {
    setMuted(!isMuted.value);
  };

  const onLoadedMetadata = () => {
    const video = videoEl.value;
    if (!video) return;
    emit('ready');
    if (video.videoWidth && video.videoHeight) {
      emit('aspect-ratio', video.videoWidth / video.videoHeight);
    }
    if (props.autoplay) {
      tryAutoplay();
    }
  };

  watch(
    () => props.autoplay,
    (shouldPlay) => {
      if (shouldPlay) tryAutoplay();
      else pause();
    }
  );

  onMounted(() => {
    if (props.autoplay && videoEl.value?.readyState >= 1) {
      tryAutoplay();
    }
  });

  onUnmounted(() => {
    pause();
  });

  defineExpose({ play, pause, playPreferringSound });
</script>
